import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSearch,
  faPlus,
  faCheck,
  faEdit,
  faTrash,
  faTag,
} from '@fortawesome/free-solid-svg-icons';
import CustomFieldsService from '@frontend/AppSettings/services/CustomfieldsService';
import { CustomField } from '@frontend/Models/CustomField/CustomFieldModels';

interface CustomFieldManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFieldIds: number[];
  onToggleField: (fieldId: number) => void;
  onFieldsChanged?: () => Promise<void> | void;
}

type FieldMessage = { type: 'success' | 'error' | ''; text: string };

const styles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(17, 24, 39, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 20,
  },
  modal: {
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: 960,
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '24px 28px 20px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fafbfc',
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 18,
    marginRight: 16,
    flexShrink: 0,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: '#111827',
    letterSpacing: '-0.025em',
  },
  headerSubtitle: {
    margin: '6px 0 0',
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 1.5,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: 'none',
    background: '#f3f4f6',
    color: '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    transition: 'all 0.15s ease',
  },
  body: {
    padding: 24,
    overflowY: 'auto' as const,
    flex: 1,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr',
    gap: 24,
  },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '18px 20px',
    borderBottom: '1px solid #f3f4f6',
    background: '#fafbfc',
  },
  cardTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardTitleIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
  },
  cardBody: {
    padding: 20,
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    marginBottom: 16,
  },
  searchIcon: {
    color: '#9ca3af',
    fontSize: 14,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: 14,
    color: '#111827',
    outline: 'none',
  },
  fieldsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
    maxHeight: '45vh',
    overflowY: 'auto' as const,
  },
  fieldCard: {
    padding: '14px 16px',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    transition: 'all 0.15s ease',
    cursor: 'pointer',
  },
  fieldCardSelected: {
    background: '#eef2ff',
    borderColor: '#6366f1',
  },
  fieldHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  fieldName: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: '#1e293b',
    flex: 1,
  },
  fieldActions: {
    display: 'flex',
    gap: 6,
  },
  actionBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: 'none',
    background: '#f3f4f6',
    color: '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    transition: 'all 0.15s ease',
  },
  selectBtn: {
    background: '#ecfdf5',
    color: '#16a34a',
  },
  deleteBtn: {
    background: '#fef2f2',
    color: '#dc2626',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '40px 20px',
    color: '#6b7280',
  },
  formInput: {
    width: '100%',
    padding: '12px 14px',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 14,
    background: '#f8fafc',
    transition: 'all 0.2s ease',
    outline: 'none',
    boxSizing: 'border-box' as const,
    marginBottom: 14,
  },
  primaryBtn: {
    flex: 1,
    padding: '12px 18px',
    background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'all 0.15s ease',
  },
  secondaryBtn: {
    padding: '12px 18px',
    background: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
};

const CustomFieldManagerModal: React.FC<CustomFieldManagerModalProps> = ({
  isOpen,
  onClose,
  selectedFieldIds,
  onToggleField,
  onFieldsChanged,
}) => {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [message, setMessage] = useState<FieldMessage>({ type: '', text: '' });
  const [newFieldName, setNewFieldName] = useState<string>('');
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [editingName, setEditingName] = useState<string>('');

  const fetchFields = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await CustomFieldsService.getCustomFields();
      setFields(data?.data || []);
    } catch (err) {
      console.error('Error fetching custom fields:', err);
      setMessage({ type: 'error', text: 'Failed to load custom fields' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchFields();
      setSearch('');
      setMessage({ type: '', text: '' });
      setNewFieldName('');
      setEditingField(null);
    }
  }, [isOpen]);

  const handleCreate = async (): Promise<void> => {
    if (!newFieldName.trim()) return;
    try {
      await CustomFieldsService.createCustomField({ custom_field: newFieldName.trim() });
      setMessage({ type: 'success', text: `Field "${newFieldName}" created!` });
      setNewFieldName('');
      await fetchFields();
      onFieldsChanged?.();
    } catch (err) {
      console.error('Error creating field:', err);
      setMessage({ type: 'error', text: 'Failed to create field' });
    }
  };

  const handleUpdate = async (fieldId: number): Promise<void> => {
    if (!editingName.trim()) return;
    try {
      await CustomFieldsService.updateCustomField(fieldId.toString(), { custom_field: editingName.trim() });
      setMessage({ type: 'success', text: 'Field updated!' });
      setEditingField(null);
      setEditingName('');
      await fetchFields();
      onFieldsChanged?.();
    } catch (err) {
      console.error('Error updating field:', err);
      setMessage({ type: 'error', text: 'Failed to update field' });
    }
  };

  const handleDelete = async (fieldId: number): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this custom field?')) return;
    try {
      await CustomFieldsService.deleteCustomField(fieldId.toString());
      setMessage({ type: 'success', text: 'Field deleted!' });
      await fetchFields();
      onFieldsChanged?.();
    } catch (err) {
      console.error('Error deleting field:', err);
      setMessage({ type: 'error', text: 'Failed to delete field' });
    }
  };

  const filteredFields = fields.filter((f) =>
    (f.custom_field || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={styles.headerIcon}>
              <FontAwesomeIcon icon={faTag} />
            </div>
            <div style={styles.headerContent}>
              <h2 style={styles.headerTitle}>Custom Fields</h2>
              <p style={styles.headerSubtitle}>
                Create new fields or manage existing ones. Select fields to include in this matter.
              </p>
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Message */}
          {message.text && (
            <div
              style={{
                marginBottom: 16,
                padding: '12px 14px',
                borderRadius: 10,
                background: message.type === 'error' ? '#fef2f2' : '#ecfdf5',
                color: message.type === 'error' ? '#b91c1c' : '#166534',
                border: `1px solid ${message.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 14,
              }}
            >
              <span>{message.text}</span>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
                onClick={() => setMessage({ type: '', text: '' })}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          )}

          <div style={styles.grid}>
            {/* Left: Fields List */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <span style={{ ...styles.cardTitleIcon, background: '#e0f2fe', color: '#0284c7' }}>
                    <FontAwesomeIcon icon={faTag} />
                  </span>
                  Your Custom Fields
                </h3>
              </div>
              <div style={styles.cardBody}>
                {/* Search */}
                <div style={styles.searchBox}>
                  <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search fields..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={styles.searchInput}
                  />
                </div>

                {/* Fields list */}
                {loading ? (
                  <div style={styles.emptyState}>Loading...</div>
                ) : filteredFields.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p style={{ margin: 0, fontSize: 14 }}>
                      {search ? 'No matching fields found' : 'No custom fields yet'}
                    </p>
                    <p style={{ margin: '8px 0 0', fontSize: 13, color: '#9ca3af' }}>
                      Create your first field using the form on the right
                    </p>
                  </div>
                ) : (
                  <div style={styles.fieldsList}>
                    {filteredFields.map((field) => {
                      const fieldId =
                        typeof field.customfield_id === 'string'
                          ? parseInt(field.customfield_id)
                          : field.customfield_id;
                      const isSelected = selectedFieldIds.includes(fieldId);
                      const isEditing = editingField?.customfield_id === field.customfield_id;

                      return (
                        <div
                          key={field.customfield_id}
                          style={{
                            ...styles.fieldCard,
                            ...(isSelected ? styles.fieldCardSelected : {}),
                          }}
                        >
                          {isEditing ? (
                            <div>
                              <input
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                style={{ ...styles.formInput, marginBottom: 10 }}
                                autoFocus
                              />
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  style={{ ...styles.primaryBtn, padding: '8px 14px', fontSize: 13 }}
                                  onClick={() => handleUpdate(fieldId)}
                                >
                                  Save
                                </button>
                                <button
                                  style={{ ...styles.secondaryBtn, padding: '8px 14px', fontSize: 13 }}
                                  onClick={() => {
                                    setEditingField(null);
                                    setEditingName('');
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={styles.fieldHeader}>
                              <h4 style={styles.fieldName}>{field.custom_field}</h4>
                              <div style={styles.fieldActions}>
                                <button
                                  style={{
                                    ...styles.actionBtn,
                                    ...(isSelected ? styles.selectBtn : {}),
                                  }}
                                  onClick={() => onToggleField(fieldId)}
                                  title={isSelected ? 'Remove from matter' : 'Add to matter'}
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                </button>
                                <button
                                  style={styles.actionBtn}
                                  onClick={() => {
                                    setEditingField(field);
                                    setEditingName(field.custom_field);
                                  }}
                                  title="Edit field"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                  style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                                  onClick={() => handleDelete(fieldId)}
                                  title="Delete field"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Create Form */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <span style={{ ...styles.cardTitleIcon, background: '#dcfce7', color: '#16a34a' }}>
                    <FontAwesomeIcon icon={faPlus} />
                  </span>
                  Create New Field
                </h3>
              </div>
              <div style={styles.cardBody}>
                {/* Step indicator */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      background: newFieldName.trim() ? '#4338ca' : '#e5e7eb',
                      color: newFieldName.trim() ? '#fff' : '#9ca3af',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    1
                  </span>
                  <label style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                    Name your custom field
                  </label>
                </div>

                <p
                  style={{
                    margin: '0 0 14px 36px',
                    fontSize: 13,
                    color: '#6b7280',
                    lineHeight: 1.5,
                  }}
                >
                  Custom fields capture additional information specific to your matters.
                </p>

                <div style={{ marginLeft: 36 }}>
                  <input
                    type="text"
                    placeholder="e.g., Case Priority, Court Jurisdiction..."
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newFieldName.trim()) {
                        e.preventDefault();
                        handleCreate();
                      }
                    }}
                    style={styles.formInput}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#4338ca';
                      e.target.style.background = '#fff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(67, 56, 202, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.background = '#f8fafc';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Preview */}
                {newFieldName.trim() && (
                  <div style={{ marginLeft: 36, marginBottom: 16 }}>
                    <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, fontWeight: 600 }}>
                      ✨ Preview
                    </p>
                    <div
                      style={{
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 10,
                        padding: 14,
                      }}
                    >
                      <label
                        style={{
                          display: 'block',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#374151',
                          marginBottom: 6,
                        }}
                      >
                        {newFieldName}
                      </label>
                      <div
                        style={{
                          height: 36,
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: 12,
                          color: '#9ca3af',
                          fontSize: 13,
                        }}
                      >
                        Enter value...
                      </div>
                    </div>
                  </div>
                )}

                {/* Tip */}
                {!newFieldName.trim() && (
                  <div
                    style={{
                      marginLeft: 36,
                      padding: '12px 14px',
                      background: '#fef3c7',
                      borderRadius: 10,
                      border: '1px solid #fcd34d',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      marginBottom: 16,
                    }}
                  >
                    <span style={{ fontSize: 16 }}>💡</span>
                    <div>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#92400e' }}>
                        Need ideas?
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: '#a16207', lineHeight: 1.5 }}>
                        <strong>Case Number</strong>, <strong>Opposing Counsel</strong>,{' '}
                        <strong>Court Name</strong>, <strong>Settlement Amount</strong>
                      </p>
                    </div>
                  </div>
                )}

                {/* Create button */}
                <div style={{ marginLeft: 36, display: 'flex', gap: 10 }}>
                  <button
                    onClick={handleCreate}
                    disabled={!newFieldName.trim()}
                    style={{
                      ...styles.primaryBtn,
                      opacity: newFieldName.trim() ? 1 : 0.5,
                      cursor: newFieldName.trim() ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Create Field
                  </button>
                  {newFieldName && (
                    <button style={styles.secondaryBtn} onClick={() => setNewFieldName('')}>
                      Clear
                    </button>
                  )}
                </div>

                {!newFieldName.trim() && (
                  <p style={{ margin: '12px 0 0 36px', fontSize: 12, color: '#9ca3af' }}>
                    Enter a field name to continue
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomFieldManagerModal;
